import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, AlertTriangle, Calendar, ArrowRight, MapPin, Copy } from 'lucide-react';
import { bookingApi } from '../services/bookingApi';

type VerificationState = 'loading' | 'success' | 'pending' | 'error';

export default function PaymentSuccessPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<VerificationState>('loading');
  const [appointment, setAppointment] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [anonymousCodeCopied, setAnonymousCodeCopied] = useState(false);

  const sessionId = searchParams.get('session_id');
  const appointmentId = searchParams.get('appointment_id');
  const provider = searchParams.get('provider');
  const transactionRef = searchParams.get('transaction_ref');
  const providerStatus = searchParams.get('status');
  const vivaTransactionId = searchParams.get('t');
  const vivaOrderCode = searchParams.get('s');

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10;

    const verify = async () => {
      try {
        const response = await bookingApi.verifyPayment({
          sessionId: sessionId || undefined,
          appointmentId: appointmentId || undefined,
          provider: provider || undefined,
          transactionRef: transactionRef || undefined,
          status: providerStatus || undefined,
          vivaTransactionId: vivaTransactionId || undefined,
          orderCode: vivaOrderCode || undefined,
        });

        if (response.success && response.data) {
          const { paymentStatus, appointment: appt } = response.data;
          setAppointment(appt);

          if (paymentStatus === 'paid' || appt?.depositStatus === 'PAID') {
            setState('success');
            return;
          }

          // Webhook may not have arrived yet — retry
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(verify, 2000);
          } else {
            setState('pending');
          }
        } else {
          setState('error');
          setErrorMessage(response.message || 'Verification failed');
        }
      } catch (err: any) {
        setState('error');
        setErrorMessage(err.message || 'Unable to verify payment');
      }
    };

    if (sessionId || appointmentId || transactionRef || vivaTransactionId || vivaOrderCode) {
      verify();
    } else {
      setState('error');
      setErrorMessage('Missing payment session information');
    }
  }, [sessionId, appointmentId, provider, transactionRef, providerStatus, vivaTransactionId, vivaOrderCode]);

  const client = appointment?.client;
  const anonymousCode = (client?.anonymousCode || '').trim();
  const isAnonymousClient = Boolean(client?.isAnonymous || anonymousCode);
  const clientFirstName = (client?.firstName || '').trim();
  const clientLastName = (client?.lastName || '').trim();
  const greetingName = isAnonymousClient
    ? clientFirstName
    : [clientFirstName, clientLastName].filter(Boolean).join(' ');

  const copyAnonymousCode = async () => {
    if (!anonymousCode) return;
    try {
      await navigator.clipboard.writeText(anonymousCode);
      setAnonymousCodeCopied(true);
      window.setTimeout(() => setAnonymousCodeCopied(false), 2000);
    } catch (copyError) {
      console.warn('Failed to copy anonymous client code:', copyError);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 py-20">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {state === 'loading' && (
            <>
              <div className="flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mx-auto mb-6">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-display font-bold text-textPrimary mb-4">
                {t('payment.success.verifying', 'Verifying your payment...')}
              </h1>
              <p className="text-textSecondary">
                {t('payment.success.pleaseWait', 'Please wait while we confirm your payment.')}
              </p>
            </>
          )}

          {state === 'success' && (
            <>
              <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="text-2xl font-display font-bold text-textPrimary mb-4">
                {t('payment.success.title', 'Payment confirmed!')}
              </h1>

              {greetingName ? (
                <p className="text-lg text-textPrimary mb-2">
                  {t('payment.success.greeting', 'Thank you, {{name}}!', {
                    name: greetingName,
                  })}
                </p>
              ) : isAnonymousClient ? (
                <p className="text-lg text-textPrimary mb-2">
                  {t('piercingBooking.clientCreated.anonymousLabel')}
                </p>
              ) : null}

              <p className="text-textSecondary mb-6">
                {isAnonymousClient
                  ? t('payment.success.anonymousDescription')
                  : t(
                      'payment.success.description',
                      'Your deposit has been received. A confirmation email with your QR code has been sent.'
                    )}
              </p>

              {isAnonymousClient && anonymousCode ? (
                <div className="mb-6 rounded-xl border border-purple-300 bg-white px-6 py-5 text-center">
                  <p className="text-sm font-medium text-purple-800 mb-2">
                    {t('payment.success.anonymousCodeLabel')}
                  </p>
                  <p className="text-2xl font-mono font-bold tracking-[0.2em] text-gray-900">
                    {anonymousCode}
                  </p>
                  <button
                    type="button"
                    onClick={copyAnonymousCode}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-700 text-white hover:bg-purple-800"
                  >
                    <Copy className="w-4 h-4" />
                    <span>
                      {anonymousCodeCopied
                        ? t('payment.success.codeCopied')
                        : t('payment.success.copyCode')}
                    </span>
                  </button>
                </div>
              ) : null}

              {appointment && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left space-y-3">
                  {appointment.type && (
                    <div className="text-sm font-medium text-primary-700 uppercase tracking-wide">
                      {appointment.type === 'PIERCING'
                        ? t('payment.success.typePiercing', 'Piercing appointment')
                        : appointment.type === 'CONSULTATION'
                          ? t('payment.success.typeConsultation', 'Tattoo consultation')
                          : t('payment.success.typeTattoo', 'Tattoo session')}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {appointment.scheduledAt
                        ? new Date(appointment.scheduledAt).toLocaleDateString('fr-BE', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : ''}
                    </span>
                  </div>
                  {appointment.shop?.name && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      <span>{appointment.shop.name}</span>
                    </div>
                  )}
                  {appointment.depositAmount && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
                      <span>
                        {t('payment.success.depositPaid', 'Deposit paid')}: <strong>{appointment.depositAmount}€</strong>
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center justify-center gap-2"
                >
                  {t('payment.success.backHome', 'Back to home')}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {state === 'pending' && (
            <>
              <div className="flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mx-auto mb-6">
                <AlertTriangle className="w-12 h-12 text-yellow-600" />
              </div>
              <h1 className="text-2xl font-display font-bold text-textPrimary mb-4">
                {t('payment.success.pendingTitle', 'Payment processing')}
              </h1>
              <p className="text-textSecondary mb-6">
                {t(
                  'payment.success.pendingDescription',
                  'Your payment is being processed. You will receive a confirmation email shortly. If you do not receive it within a few minutes, please contact us.'
                )}
              </p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                {t('payment.success.backHome', 'Back to home')}
              </button>
            </>
          )}

          {state === 'error' && (
            <>
              <div className="flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mx-auto mb-6">
                <AlertTriangle className="w-12 h-12 text-red-600" />
              </div>
              <h1 className="text-2xl font-display font-bold text-textPrimary mb-4">
                {t('payment.success.errorTitle', 'Verification error')}
              </h1>
              <p className="text-textSecondary mb-6">
                {errorMessage || t('payment.success.errorDescription', 'We could not verify your payment. Please contact us.')}
              </p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                {t('payment.success.backHome', 'Back to home')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
